import { Module, MutationTree, ActionTree, GetterTree } from 'vuex'

interface Tour {
  id: string
  name: string
  destination: string
  duration: number
  price: number
  rating: number
  reviews: number
  description: string
  image: string
  activities: string[]
  bestSeason: string
  difficulty: 'easy' | 'moderate' | 'challenging'
  groupSize: string
  featured: boolean
}

interface TourState {
  tours: Tour[]
  filteredTours: Tour[]
  selectedTour: Tour | null
  favorites: string[]
  filters: {
    destination?: string
    difficulty?: string
    minPrice?: number
    maxPrice?: number
    featured?: boolean
  }
}

const state: TourState = {
  tours: [],
  filteredTours: [],
  selectedTour: null,
  favorites: [],
  filters: {}
}

const getters: GetterTree<TourState, any> = {
  allTours: (state) => state.tours,
  filteredTours: (state) => state.filteredTours,
  selectedTour: (state) => state.selectedTour,
  tourCount: (state) => state.tours.length,
  favoriteCount: (state) => state.favorites.length,
  favoriteTours: (state) => state.tours.filter(t => state.favorites.includes(t.id)),
  toursByDestination: (state) => (destination: string) => state.tours.filter(t => t.destination === destination),
  getTourById: (state) => (id: string) => state.tours.find(t => t.id === id),
  getCurrentFilters: (state) => state.filters
}

const mutations: MutationTree<TourState> = {
  SET_TOURS: (state, tours: Tour[]) => {
    state.tours = tours
    state.filteredTours = tours
  },
  ADD_TOUR: (state, tour: Tour) => {
    state.tours.push(tour)
  },
  UPDATE_TOUR: (state, updatedTour: Tour) => {
    const index = state.tours.findIndex(t => t.id === updatedTour.id)
    if (index !== -1) {
      state.tours[index] = updatedTour
    }
  },
  DELETE_TOUR: (state, tourId: string) => {
    state.tours = state.tours.filter(t => t.id !== tourId)
  },
  SET_SELECTED_TOUR: (state, tour: Tour | null) => {
    state.selectedTour = tour
  },
  ADD_FAVORITE: (state, tourId: string) => {
    if (!state.favorites.includes(tourId)) {
      state.favorites.push(tourId)
    }
  },
  REMOVE_FAVORITE: (state, tourId: string) => {
    state.favorites = state.favorites.filter(id => id !== tourId)
  },
  SET_FILTERED_TOURS: (state, tours: Tour[]) => {
    state.filteredTours = tours
  },
  SET_FILTERS: (state, filters: any) => {
    state.filters = filters
  },
  APPLY_FILTERS: (state) => {
    let filtered = state.tours
    const f = state.filters as any

    if (f.destination) {
      const destination = f.destination.toLowerCase()
      filtered = filtered.filter(t => t.destination && t.destination.toLowerCase() === destination)
    }
    if (f.difficulty) {
      filtered = filtered.filter(t => t.difficulty === f.difficulty)
    }
    const minPrice = typeof f.minPrice === 'number' ? f.minPrice : undefined
    const maxPrice = typeof f.maxPrice === 'number' ? f.maxPrice : undefined

    if (minPrice !== undefined) {
      filtered = filtered.filter(t => (t.price ?? 0) >= minPrice)
    }
    if (maxPrice !== undefined) {
      filtered = filtered.filter(t => (t.price ?? 0) <= maxPrice)
    }
    if (f.featured === true) {
      filtered = filtered.filter(t => t.featured === true)
    }

    state.filteredTours = filtered
  }
}

const actions: ActionTree<TourState, any> = {
  setTours ({ commit }, tours: Tour[]) {
    commit('SET_TOURS', tours)
  },
  addTour ({ commit }, tour: Tour) {
    commit('ADD_TOUR', tour)
  },
  updateTour ({ commit }, tour: Tour) {
    commit('UPDATE_TOUR', tour)
  },
  deleteTour ({ commit }, tourId: string) {
    commit('DELETE_TOUR', tourId)
  },
  selectTour ({ commit }, tour: Tour | null) {
    commit('SET_SELECTED_TOUR', tour)
  },
  toggleFavorite ({ commit, state }, tourId: string) {
    if (state.favorites.includes(tourId)) {
      commit('REMOVE_FAVORITE', tourId)
    } else {
      commit('ADD_FAVORITE', tourId)
    }
  },
  applyFilters ({ commit }, filters: any) {
    commit('SET_FILTERS', filters)
    commit('APPLY_FILTERS')
  },
  clearFilters ({ commit }) {
    commit('SET_FILTERS', {})
    commit('APPLY_FILTERS')
  }
}

const tourModule: Module<TourState, any> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export default tourModule
